import api from './api';
import { Note, NoteBlock, NotePermission, NoteVisibility } from '../types';

export interface NoteListFilters {
  view?: 'all' | 'mine' | 'shared' | 'favorites' | 'trash';
  search?: string;
  tag?: string;
}

export interface NoteMemberInput {
  userId: string;
  permission: Exclude<NotePermission, 'OWNER'>;
}

export interface CreateNoteInput {
  title: string;
  content?: NoteBlock[];
  tags?: string[];
  visibility?: NoteVisibility;
  members?: NoteMemberInput[];
}

export interface UpdateNoteInput {
  title?: string;
  content?: NoteBlock[];
  tags?: string[];
  visibility?: NoteVisibility;
}

export async function getNotes(filters: NoteListFilters = {}): Promise<Note[]> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
  const response = await api.get(`/api/notes${params.size ? `?${params}` : ''}`);
  return response.data.data;
}

export async function getNote(id: string): Promise<Note> {
  const response = await api.get(`/api/notes/${id}`);
  return response.data.data;
}

export async function createNote(input: CreateNoteInput): Promise<Note> {
  const response = await api.post('/api/notes', input);
  return response.data.data;
}

export async function updateNote(id: string, input: UpdateNoteInput): Promise<Note> {
  const response = await api.put(`/api/notes/${id}`, input);
  return response.data.data;
}

export async function saveNoteSecretBlock(id: string, blockId: string, label: string, value: string): Promise<void> {
  await api.put(`/api/notes/${id}/secret-blocks/${blockId}`, { label, value });
}

export async function revealNoteSecretBlock(id: string, blockId: string): Promise<string> {
  const response = await api.post(`/api/notes/${id}/secret-blocks/${blockId}/reveal`);
  return response.data.data.value;
}

export async function deleteNoteSecretBlock(id: string, blockId: string): Promise<void> {
  await api.delete(`/api/notes/${id}/secret-blocks/${blockId}`);
}

export async function recordNoteSecretCopy(id: string): Promise<void> {
  await api.post(`/api/notes/${id}/secret-copy`);
}

export async function updateNoteSharing(id: string, members: NoteMemberInput[]): Promise<Note> {
  const response = await api.put(`/api/notes/${id}/sharing`, { members });
  return response.data.data;
}

export async function setNoteFavorite(id: string, favorite: boolean): Promise<void> {
  await api.put(`/api/notes/${id}/favorite`, { favorite });
}

export async function trashNote(id: string): Promise<void> {
  await api.delete(`/api/notes/${id}`);
}

export async function restoreNote(id: string): Promise<void> {
  await api.post(`/api/notes/${id}/restore`);
}

export async function deleteNotePermanently(id: string): Promise<void> {
  await api.delete(`/api/notes/${id}/permanent`);
}
